/**
 * Tolerant readers for block props.
 *
 * Block schemas are 3C's (`site/types.ts` says why this matters), and the
 * renderer runs on a firm's public website. So nothing here throws and nothing
 * here trusts a shape: a string that arrived as a number is rendered as a
 * string, an array that arrived as an object is an empty list, and a block that
 * ends up with nothing to say renders nothing at all.
 */

export function str(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

export function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function obj(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/**
 * A link this page may emit.
 *
 * ADR-0013 §4: no block accepts HTML or script — and a `href` is the one place
 * a script can still arrive as *data*, through `javascript:` or a `data:` URL.
 * So an href is rendered only when it is site-relative or parses as http,
 * https or mailto. Anything else renders as plain text with no link at all,
 * which loses a link and cannot lose a visitor.
 */
export function href(value: unknown): string | null {
  const raw = str(value)
  if (!raw) return null
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw
  try {
    const parsed = new URL(raw)
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:' || parsed.protocol === 'mailto:') {
      return parsed.href
    }
  } catch {
    return null
  }
  return null
}

/** The same rule for an image `src`, which may also be a site-relative path. */
export function src(value: unknown): string | null {
  return href(value)
}

/**
 * A positive whole number, or `null`.
 *
 * For the two dimensions an `image` block carries (Wave 3 close §6.1). A
 * number that arrived as a string is read; a zero, a negative, a fraction or a
 * `NaN` is `null`, because each of them as an `<img width>` is worse than no
 * attribute at all — the browser's own fallback is at least a sane box.
 */
export function num(value: unknown): number | null {
  const raw = typeof value === 'number' ? value : Number(str(value))
  return Number.isInteger(raw) && raw > 0 ? raw : null
}

/**
 * A fee, formatted the way the rest of the product formats one. Called only
 * after the policy has said prices may be shown (`site/policy.ts`).
 */
export function rupees(value: unknown): string | null {
  const raw = str(value)
  if (!raw) return null
  const amount = Number(raw.replace(/,/g, ''))
  if (!Number.isFinite(amount)) return raw
  return `₹${amount.toLocaleString('en-IN')}`
}
